import React, { useState, useEffect, ChangeEvent, useMemo, useCallback } from 'react'
import { useWatch, Controller } from 'react-hook-form'
import { useSelector, useDispatch } from 'react-redux'
import ControlledHiddenInput from 'transfix-form/components/ControlledHiddenInput'
import { DeprecatedControlledSelect } from 'transfix-form/components/ControlledSelect'
import { DeprecatedControlledTextInput } from 'transfix-form/components/ControlledTextInput'
import { PhoneInput } from 'transfix-ui/components/PhoneInput'
import { Box } from 'transfix-ui/core'
import { ICarrierState, ICarrierContactState, ICarrierContact } from 'types/carrier'
import {
  closeContactInformationDialog,
  editContactInfo,
} from 'apps/carrier/utils/actions/carrierContact'
import { IStore } from 'apps/carrier/utils/reducers'
import { FormModal } from 'transfix-ui'
import { STATES, COUNTRIES } from 'constants/countriesStates'
import useContactInfoTypeForm from './hooks/useContactInfoForm'
import { mapContactInfoToCarrierContact } from './schema'

interface ISelector {
  carrier: ICarrierState
  carrierContact: ICarrierContactState
}

const usStates = STATES.US.map(state => ({ label: state.name, value: state.abbr }))
const canadianStates = STATES.CA.map(state => ({ label: state.name, value: state.abbr }))
const countries = COUNTRIES.map(country => ({ label: country.name, value: country.code }))

const ContactDialog = () => {
  const dispatch = useDispatch()
  const {
    carrier: { carrierDetails },
    carrierContact: { contact, dialogError, showDialog, updating },
  } = useSelector<IStore, ISelector>(({ carrier, carrierContact }) => ({ carrier, carrierContact }))
  const closeDialog = () => {
    dispatch(closeContactInformationDialog())
  }

  const [selectableStates, setSelectableStates] = useState(usStates)

  const submitHandler = useCallback(
    data => {
      dispatch(editContactInfo(carrierDetails?.id || '', mapContactInfoToCarrierContact(data)))
    },
    [dispatch, carrierDetails?.id]
  )

  const getContactInfo = (contactInfo: ICarrierContact) => ({
    id: contactInfo.location?.id,
    contactEmail: contactInfo.contactEmail,
    contactPhone: contactInfo.location?.contactPhone,
    contactPhoneExt: contactInfo.location?.contactPhoneExt,
    address: contactInfo.location?.address,
    addressLine2: contactInfo.location?.addressLine2,
    countryCode: contactInfo.location?.countryCode,
    city: contactInfo.location?.city,
    stateCode: contactInfo.location?.stateCode,
    zipcode: contactInfo.location?.zipcode,
  })

  const defaultValues = useMemo(() => getContactInfo(contact), [contact])
  const { control, errors, handleSubmit } = useContactInfoTypeForm({ defaultValues })

  const values = useWatch({
    control,
    name: ['contactPhone', 'countryCode', 'stateCode'],
    defaultValue: defaultValues,
  })

  /**
   * This useEffect it's used it for setting the corresponding options to the stateCode selector
   */
  useEffect(() => {
    setSelectableStates(values.countryCode === 'CA' ? canadianStates : usStates)
  }, [values])

  return (
    <FormModal
      title='Edit Contact Information'
      size={6}
      open={showDialog}
      onClose={closeDialog}
      error={dialogError}
      dataTest='modal-edit-company-information'
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          disabled: updating,
        },
        {
          label: 'Save',
          type: 'confirm',
          disabled: updating,
          action: handleSubmit(submitHandler),
        },
      ]}>
      <Box className='row'>
        <ControlledHiddenInput control={control} name='id' />
        <Box className='col-xs-6'>
          <DeprecatedControlledTextInput
            name='contactEmail'
            TextInputProps={{
              type: 'text',
              placeholder: 'Contact Email',
              label: 'Carrier Contact Email',
              dataTest: 'contact-email',
              invalid: !!errors.contactEmail,
              validationMessage: errors.contactEmail?.message,
              required: true,
            }}
            control={control}
          />
        </Box>
        <Box className='col-xs-4'>
          <Controller
            name='contactPhone'
            control={control}
            defaultValue={values.contactPhone}
            render={({ onChange }) => (
              <PhoneInput
                placeholder='(555) 555-5555'
                label='Phone'
                id='contactPhone'
                value={values.contactPhone}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  onChange(e.target.value)
                }}
              />
            )}
          />
        </Box>
        <Box className='col-xs-2'>
          <DeprecatedControlledTextInput
            name='contactPhoneExt'
            TextInputProps={{
              type: 'number',
              placeholder: 'Ext',
              label: 'Ext',
              dataTest: 'contact-phone-ext',
              invalid: !!errors.contactPhoneExt,
              validationMessage: errors.contactPhoneExt?.message,
            }}
            control={control}
          />
        </Box>
      </Box>
      <Box className='row'>
        <Box className='col-xs-6'>
          <DeprecatedControlledTextInput
            name='address'
            TextInputProps={{
              type: 'text',
              placeholder: 'Billing Address',
              label: 'Billing Address',
              dataTest: 'billing-address',
              invalid: !!errors.address,
              validationMessage: errors.address?.message,
              required: true,
            }}
            control={control}
          />
        </Box>
        <Box className='col-xs-5'>
          <DeprecatedControlledTextInput
            name='addressLine2'
            TextInputProps={{
              type: 'text',
              placeholder: 'Apt, suite, unit, building, floor, etc.',
              label: 'Billing Address 2',
              dataTest: 'address-line-2',
              invalid: !!errors.addressLine2,
              validationMessage: errors.addressLine2?.message,
            }}
            control={control}
          />
        </Box>
      </Box>
      <Box className='row'>
        <Box className='col-xs-6'>
          <DeprecatedControlledSelect
            control={control}
            name='countryCode'
            SelectProps={{
              large: true,
              options: countries,
              label: 'Country',
              required: true,
            }}
          />
        </Box>
        <Box className='col-xs-5'>
          <DeprecatedControlledSelect
            control={control}
            name='stateCode'
            SelectProps={{
              large: true,
              options: selectableStates,
              label: values.countryCode === 'CA' ? 'Province' : 'State',
              validationMessage: errors.stateCode?.message,
              invalid: !!errors.stateCode,
              required: true,
            }}
          />
        </Box>
      </Box>
      <Box className='row'>
        <Box className='col-xs-6'>
          <DeprecatedControlledTextInput
            name='city'
            TextInputProps={{
              type: 'text',
              placeholder: 'City',
              label: 'City',
              dataTest: 'city',
              invalid: !!errors.city,
              validationMessage: errors.city?.message,
              required: true,
            }}
            control={control}
          />
        </Box>

        <Box className='col-xs-3'>
          <DeprecatedControlledTextInput
            name='zipcode'
            TextInputProps={{
              type: 'text',
              placeholder: 'Postal Code',
              label: 'Postal Code',
              dataTest: 'postal-code',
              invalid: !!errors.zipcode,
              validationMessage: errors.zipcode?.message,
              required: true,
            }}
            control={control}
          />
        </Box>
      </Box>
    </FormModal>
  )
}

export default ContactDialog
